import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export type TextSizeValue = 'S' | 'M' | 'L';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    textSize: {
      setTextSize: (size: TextSizeValue) => ReturnType;
      unsetTextSize: () => ReturnType;
      toggleTextSize: (size: TextSizeValue) => ReturnType;
    };
  }
}

export const TextSize = Mark.create({
  name: 'textSize',

  addAttributes() {
    return {
      size: {
        default: 'M',
        parseHTML: (element: HTMLElement) => {
          const size = element.getAttribute('data-size');
          return size === 'S' || size === 'L' ? size : 'M';
        },
        renderHTML: (attributes: { size?: TextSizeValue }) => {
          const { size } = attributes;
          return size === 'S' || size === 'L' ? { 'data-size': size } : {};
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-size]' }];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('mediumTextLineHeight'),
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isText) return;
              const size = node.marks.find((mark) => mark.type.name === 'textSize')?.attrs.size;
              if (size === 'S' || size === 'L') return;
              decorations.push(Decoration.inline(pos, pos + node.nodeSize, {
                nodeName: 'span',
                'data-size': 'M',
              }));
            });
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setTextSize: (size: TextSizeValue) => ({ commands }) => {
        if (size === 'M') {
          return commands.unsetMark(this.name);
        }
        return commands.setMark(this.name, { size });
      },
      unsetTextSize: () => ({ commands }) => commands.unsetMark(this.name),
      toggleTextSize: (size: TextSizeValue) => ({ commands, editor }) => {
        if (size === 'M') {
          return commands.unsetMark(this.name);
        }
        if (editor.isActive(this.name, { size })) {
          return commands.unsetMark(this.name);
        }
        return commands.setMark(this.name, { size });
      },
    };
  },
});
